import { FORMAT, GEAR } from '@/utils/constants';

/**
 * What the player shows beside a tone, by gear and format.
 *
 * The engine's chain is fixed: `pre NAM → NAM → IR`. A tone occupies one
 * slot (its model, or its IR) and gets exactly one companion select. The
 * format decides *where* the companion can go; the gear decides *what* it
 * is and what it defaults to.
 *
 * | Gear         | NAM tone → companion      | IR tone → companion (before the IR) | IR mix   |
 * | ------------ | ------------------------- | ----------------------------------- | -------- |
 * | amp          | Cab · user's cab          | —                                   | —        |
 * | amp-cab      | Reverb · none             | —                                   | —        |
 * | pedal        | Cab · user's pedal cab    | Amp · none                          | by length|
 * | outboard     | Cab · none                | Amp · none                          | by length|
 * | cab          | Amp (ahead of it) · none  | Amp · user's amp                    | 1        |
 * | space        | —                         | Amp · user's amp                    | 0.5      |
 * | experimental | Cab · none                | Amp · none                          | by length|
 *
 * "user's pedal cab" is remembered apart from the amp cab and starts at None,
 * so a pedal is heard alone until the user picks a cab for one.
 *
 * "by length" is the engine's rule (and the plugin's): an IR over 1 s starts
 * at 50 % wet with a mix knob, a shorter one is fully wet. `cab` and `space`
 * pin it so a long cab or a short room still behave like their gear says.
 */

/** Which demo list the companion is drawn from. */
export type CompanionPool = 'cab' | 'reverb' | 'amp';

export interface Companion {
  /** Select caption. */
  label: string;
  pool: CompanionPool;
  /**
   * "None" (hear the tone alone), the user's per-instrument pick, or the
   * user's per-instrument pedal pick (None until they choose a cab).
   */
  default: 'none' | 'user' | 'pedal';
}

export interface GearRule {
  /** Companion when the tone is a NAM capture. */
  nam?: Companion;
  /** Companion when the tone is an IR pack. */
  ir?: Companion;
  /** Wet/dry for the tone's own IRs. Omit for the engine's length rule. */
  irMix?: number;
}

const CAB: Companion = { label: 'Cab', pool: 'cab', default: 'none' };
const USER_CAB: Companion = { ...CAB, default: 'user' };
const PEDAL_CAB: Companion = { ...CAB, default: 'pedal' };
const REVERB: Companion = { label: 'Reverb', pool: 'reverb', default: 'none' };
const AMP: Companion = { label: 'Amp', pool: 'amp', default: 'none' };
const USER_AMP: Companion = { ...AMP, default: 'user' };

export const GEAR_MATRIX: Record<GEAR, GearRule> = {
  [GEAR.AMP]: { nam: USER_CAB },
  [GEAR.AMP_CAB]: { nam: REVERB },
  [GEAR.PEDAL]: { nam: PEDAL_CAB, ir: AMP },
  [GEAR.OUTBOARD]: { nam: CAB, ir: AMP },
  [GEAR.CAB]: { nam: AMP, ir: USER_AMP, irMix: 1 },
  [GEAR.SPACE]: { ir: USER_AMP, irMix: 0.5 },
  [GEAR.EXPERIMENTAL]: { nam: CAB, ir: AMP },
};

/** For gear/format pairs the catalog shouldn't produce (or unknown gear). */
const FALLBACK: Required<Pick<GearRule, 'nam' | 'ir'>> = { nam: CAB, ir: AMP };

/** The companion for this tone, never undefined. */
export const companionFor = (gear: GEAR | undefined, format: FORMAT.NAM | FORMAT.IR): Companion => {
  const rule = gear ? GEAR_MATRIX[gear] : undefined;
  return (
    (format === FORMAT.NAM ? rule?.nam : rule?.ir) ?? FALLBACK[format === FORMAT.NAM ? 'nam' : 'ir']
  );
};

/** Pinned wet/dry for an IR tone's own IRs, or undefined for the length rule. */
export const irMixFor = (gear: GEAR | undefined): number | undefined =>
  gear ? GEAR_MATRIX[gear]?.irMix : undefined;
